import { useMemo, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { DEFAULT_LOCALE } from "./locales";
import { MESSAGES } from "./messages";

// The backend stamps each tool call with English labels ("Searching the web...",
// "Searched the web"). Built-in ones are listed, verbatim, under
// ``widget.toolLabels`` in the English catalog, so a label that matches one is
// swapped for its translation and anything else — a label an agent author wrote
// themselves, or one this catalog does not know — is shown as it came.
type ToolLabelKey =
  keyof (typeof MESSAGES)[typeof DEFAULT_LOCALE]["widget"]["toolLabels"];

export type ToolLabelTranslator = (
  key: ToolLabelKey,
  values?: { name: string }
) => string;

const NAME_TOKEN = "{name}";

// The catalog is ICU MessageFormat, where a literal apostrophe is written as
// two; the backend's raw labels have single ones.
function icuToPlain(text: string): string {
  return text.replace(/''/g, "'");
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const exactLabels = new Map<string, ToolLabelKey>();
const namedLabels: Array<{ key: ToolLabelKey; pattern: RegExp }> = [];

for (const [key, english] of Object.entries(
  MESSAGES[DEFAULT_LOCALE].widget.toolLabels
) as Array<[ToolLabelKey, string]>) {
  if (english.includes(NAME_TOKEN)) {
    const [before, after] = icuToPlain(english).split(NAME_TOKEN);
    namedLabels.push({
      key,
      pattern: new RegExp(
        `^${escapeRegExp(before)}(.+)${escapeRegExp(after)}$`
      ),
    });
  } else {
    exactLabels.set(icuToPlain(english), key);
  }
}

export function translateToolLabel(
  label: string,
  translate: ToolLabelTranslator
): string {
  const exact = exactLabels.get(label);
  if (exact) return translate(exact);
  for (const { key, pattern } of namedLabels) {
    const match = pattern.exec(label);
    if (match) return translate(key, { name: match[1] });
  }
  return label;
}

const LABEL_FIELDS = [
  "display_message_start",
  "display_message_end",
  "tool_display_message_start",
  "tool_display_message_end",
] as const;

// Tool parts carry their labels under ``callProviderMetadata.noxus``; the
// workflow and co-worker data parts carry theirs directly on ``data``.
const LABELLED_DATA_PARTS = new Set(["data-run_workflow", "data-call_agent"]);

interface LabelledPart {
  type: string;
  callProviderMetadata?: { noxus?: Record<string, unknown> };
  data?: Record<string, unknown>;
}

function translateFields(
  record: Record<string, unknown>,
  translate: ToolLabelTranslator
): Record<string, unknown> | null {
  let changed: Record<string, unknown> | null = null;
  for (const field of LABEL_FIELDS) {
    const value = record[field];
    if (typeof value !== "string") continue;
    const translated = translateToolLabel(value, translate);
    if (translated === value) continue;
    changed ??= { ...record };
    changed[field] = translated;
  }
  return changed;
}

function localizePart<P extends LabelledPart>(
  part: P,
  translate: ToolLabelTranslator
): P {
  if (part.type.startsWith("tool-")) {
    const noxus = part.callProviderMetadata?.noxus;
    if (!noxus) return part;
    const changed = translateFields(noxus, translate);
    if (!changed) return part;
    return {
      ...part,
      callProviderMetadata: { ...part.callProviderMetadata, noxus: changed },
    };
  }
  if (LABELLED_DATA_PARTS.has(part.type) && part.data) {
    const changed = translateFields(part.data, translate);
    if (!changed) return part;
    return { ...part, data: changed };
  }
  return part;
}

function localizeMessage<P extends LabelledPart, M extends { parts: P[] }>(
  message: M,
  translate: ToolLabelTranslator
): M {
  let parts: P[] | null = null;
  message.parts.forEach((part, index) => {
    const localized = localizePart(part, translate);
    if (localized === part) return;
    parts ??= [...message.parts];
    parts[index] = localized;
  });
  return parts ? { ...message, parts } : message;
}

export function useLocalizedToolLabels<
  P extends LabelledPart,
  M extends { parts: P[] },
>(messages: M[]): M[] {
  const locale = useLocale();
  const t = useTranslations("widget.toolLabels");
  // Per source message, so a message the stream did not touch keeps the same
  // localized object and its memoized render.
  const [localizedBySource] = useState(
    () => new WeakMap<M, { t: typeof t; localized: M }>()
  );
  return useMemo(() => {
    if (locale === DEFAULT_LOCALE) return messages;
    const translate: ToolLabelTranslator = (key, values) =>
      values ? t(key, values) : t(key);
    return messages.map((message) => {
      const cached = localizedBySource.get(message);
      if (cached?.t === t) return cached.localized;
      const localized = localizeMessage(message, translate);
      localizedBySource.set(message, { t, localized });
      return localized;
    });
  }, [messages, locale, t, localizedBySource]);
}
